import { camelCaseResponse, providesList } from 'shared/Functions'
import { emptyApi } from './emptyApi'
import {
  SubscriberFromApi,
  CenterSubscriberFromApi,
  SubscriberFormType,
  Subscriber,
  CenterSubscriber,
  SubscriberApiResponse
} from 'types'

export const subscriberService = emptyApi.injectEndpoints({
  endpoints: (builder) => ({
    getSubscribers: builder.query<SubscriberFromApi[], void>({
      query: () => '/subscribers?center=false',
      transformResponse: (response: unknown) =>
        camelCaseResponse(
          response as Record<string, unknown>
        ) as SubscriberFromApi[],
      providesTags: (results) => providesList(results, 'Subscriber')
    }),
    getCenterSubscribers: builder.query<CenterSubscriberFromApi[], void>({
      query: () => '/subscribers?center=true',
      transformResponse: (response: unknown) =>
        camelCaseResponse(
          response as Record<string, unknown>
        ) as CenterSubscriberFromApi[],
      providesTags: (results) => providesList(results, 'CenterSubscribers')
    }),
    getAllSubscribers: builder.query<SubscriberApiResponse[], void>({
      query: () => '/admin/subscribers/get_all_subscribers',
      transformResponse: (response: unknown) => {
        // Return the camelCased response directly to preserve all fields including center
        return camelCaseResponse(
          response as Record<string, unknown>
        ) as SubscriberApiResponse[]
      }
    }),
    getSubscriberByEmail: builder.query<CenterSubscriberFromApi, string>({
      query: (email: string) =>
        `/admin/subscribers/${encodeURIComponent(email)}`,
      transformResponse: (response: unknown) =>
        camelCaseResponse(
          response as Record<string, unknown>
        ) as CenterSubscriberFromApi
    }),
    postSubscriber: builder.mutation<Record<string, unknown>, Subscriber>({
      query: (subscriberInfo: Subscriber) => ({
        url: '/subscriber',
        method: 'POST',
        body: processSubscriberForm(subscriberInfo)
      }),
      transformResponse: (response: unknown) =>
        camelCaseResponse(response as Record<string, unknown>) as
          | SubscriberFromApi
          | CenterSubscriberFromApi
    }),
    postCenterSubscriber: builder.mutation<
      Record<string, unknown>,
      CenterSubscriber
    >({
      query: (centerSubscriberInfo: CenterSubscriber) => ({
        url: '/subscriber',
        method: 'POST',
        body: centerSubscriberInfo
      }),
      transformResponse: (response: unknown) =>
        camelCaseResponse(
          response as Record<string, unknown>
        ) as CenterSubscriberFromApi
    }),
    patchSubscriber: builder.mutation<
      Record<string, unknown>,
      Record<string, unknown>
    >({
      query: (listsToAddInfo: Record<string, unknown>) => ({
        url: '/admin/sendgrid/contact_list',
        method: 'PATCH',
        body: processListsToAdd(listsToAddInfo)
      }),
      transformResponse: (response: unknown) =>
        camelCaseResponse(response as Record<string, unknown>) as Record<
          string,
          unknown
        >
    })
  })
})

export const {
  useGetSubscribersQuery,
  useGetCenterSubscribersQuery,
  useGetAllSubscribersQuery,
  useGetSubscriberByEmailQuery,
  usePatchSubscriberMutation,
  usePostCenterSubscriberMutation,
  usePostSubscriberMutation
} = subscriberService

function processSubscriberForm(subscriberInfo: SubscriberFormType) {
  return {
    first_name: subscriberInfo.firstName,
    last_name: subscriberInfo.lastName,
    email: subscriberInfo.email,
    center_name: subscriberInfo.centerName
  }
}

function processListsToAdd(listsToAddInfo: Record<string, unknown>) {
  return {
    email_list_ids: (
      listsToAddInfo.emailListIds as Array<{ value: string }>
    ).map((listInfo) => listInfo.value),
    subscriber_id: listsToAddInfo.subscriberId
  }
}
